import { Image } from "@-/dev.improve.haniverse.components.ui.image";
import type { LayoutChangeEvent } from "@-/com.npmjs.react-native";
import { Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { MorphemeMnemonicsMeaningViewImageSectionGate } from "#use-morpheme-mnemonics-meaning-view-image-section-gate";

/**
 * How the illustration occupies the scroll content:
 * - `scroll`: a full-width square spacer reserves the scroll range and the visible image grows with
 *   the scroll offset (Browse, and a card once it has been expanded).
 * - `fit`: the spacer is exactly `imageHeight` tall, so a collapsed card never overflows; the image
 *   fills whatever space is left below the prose.
 * - `hidden`: out of flow and invisible (still loading, or collapsed behind the card's Show More).
 */
export type MorphemeMnemonicsMeaningViewImageMode = "fit" | "hidden" | "scroll";

/** `h-6` row of the expand hint; it sits above the image inside the same `imageHeight` slot. */
const EXPAND_HINT_HEIGHT = 24;

/** Optional meaning illustration, placed after the mnemonic description. */
export function MorphemeMnemonicsMeaningViewImageSection({
  gate,
  imageHeight,
  imageWidth,
  isLoaded,
  mode,
  onError,
  onLayout,
  onLoad,
  onPress,
  showsExpandHint,
}: {
  gate: MorphemeMnemonicsMeaningViewImageSectionGate;
  imageHeight: number;
  imageWidth: number;
  isLoaded: boolean;
  mode: MorphemeMnemonicsMeaningViewImageMode;
  onError: () => void;
  onLayout: (event: LayoutChangeEvent) => void;
  onLoad: () => void;
  onPress: () => void;
  showsExpandHint: boolean;
}) {
  const { meaningImageUrlPath } = gate;
  const resolvedMode = isLoaded ? mode : "hidden";
  const showsHint = showsExpandHint && resolvedMode !== "hidden";
  return (
    // Reserve space only after the illustration loads. While hidden, the spacer stays out of flow
    // (so it neither adds a gap nor scroll space) but keeps its width measurable so the absolutely
    // positioned image can load. In `scroll` mode the square gives the ScrollView room to reveal
    // the illustration, while the absolutely positioned inner view scales the real image to the
    // space currently left below the prose. Keeping those two jobs separate avoids a height ->
    // content-size -> scroll feedback loop.
    <View
      className={
        resolvedMode === "scroll"
          ? "relative aspect-square w-full max-w-120 self-center"
          : resolvedMode === "fit"
            ? "relative w-full max-w-120 self-center"
            : "absolute inset-x-0 top-0 h-0 w-full opacity-0"
      }
      onLayout={onLayout}
      pointerEvents={resolvedMode === "hidden" ? "none" : "auto"}
      style={resolvedMode === "fit" ? { height: imageHeight } : undefined}
      testID="meaning-hero-image-layout-spacer"
    >
      <View className="absolute inset-x-0 top-0 items-center" style={{ height: imageHeight }}>
        {showsHint ? (
          <Pressable
            accessibilityRole="button"
            className="h-6 justify-center"
            onPress={onPress}
            testID="meaning-hero-image-expand-hint"
          >
            <Text className="text-[11px] font-medium text-muted-foreground" numberOfLines={1}>
              Tap image to expand
            </Text>
          </Pressable>
        ) : null}
        <Pressable
          accessibilityLabel="Expand image"
          accessibilityRole="button"
          className="aspect-square overflow-hidden rounded-xs bg-muted/20"
          onPress={onPress}
          style={{
            height: showsHint ? Math.max(0, imageHeight - EXPAND_HINT_HEIGHT) : imageHeight,
          }}
          testID="meaning-hero-image-container"
        >
          <Image
            className="size-full"
            contentFit="contain"
            // The outer square, not the scrolling height, bounds native PNG decoding. This keeps
            // thumbnail cache keys stable while native post-resize still follows the visible size.
            decodeTargetSize={
              Platform.OS === "ios" ? { height: imageWidth, width: imageWidth } : undefined
            }
            key={meaningImageUrlPath}
            onError={onError}
            onLoad={onLoad}
            source={{ uri: meaningImageUrlPath }}
            testID="meaning-hero-image"
          />
        </Pressable>
      </View>
    </View>
  );
}
